import {format} from 'date-fns';
import React, {useEffect, useState} from 'react';
import Calendar from 'shared/components/calendar';
import {FilterQueryProps} from 'shared/util/encode-filters';
import {ptBR} from 'date-fns/locale';
import {Popover} from '@mantine/core';

import {Container, Option, CalendarIcon, Row} from './styles';

export enum FilterEnum {
  custom = 'custom',
  today = 'today',
  yesterday = 'yesterday',
  last3hours = 'last3hours',
}

export enum DateOperators {
  Equal = '=',
  NotEqual = '≠',
  GreaterThanEqual = 'gte',
  LessThanEqual = 'lte',
}

export type OnChangeDateProps = (dateFilters: FilterQueryProps) => void;

interface Props {
  filter?: FilterEnum;
  onFilterChange: (
    filter: FilterEnum,
    dateInterval?: {startDate: Date; endDate: Date},
  ) => void;
  defaultValues?: {
    filter: FilterEnum;
    dateInterval?: {startDate: Date; endDate: Date};
  };
}

const DateFilter: React.FC<Props> = ({
  onFilterChange,
  defaultValues,
  ...props
}) => {
  const [filter, setFilter] = useState<FilterEnum>(
    props.filter ?? FilterEnum.today,
  );

  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();

  const [isShowingCalendar, showCalendar] = useState(false);

  const closeCalendar = () => showCalendar(false);

  const submitFilter = (
    filter: FilterEnum,
    dateInterval?: {startDate: Date; endDate: Date},
  ) => {
    setFilter(filter);

    if (filter !== FilterEnum.custom) {
      onFilterChange(filter);
      setStartDate(undefined);
      setEndDate(undefined);
      return;
    }

    onFilterChange(filter, dateInterval);

    setStartDate(dateInterval?.startDate);
    setEndDate(dateInterval?.endDate);
  };

  useEffect(() => {
    closeCalendar();
  }, [startDate, endDate]);

  return (
    <Row>
      <Container>
        <Popover
          withinPortal
          opened={isShowingCalendar}
          onClose={closeCalendar}
          styles={{
            dropdown: {
              padding: 0,
              backgroundColor: 'transparent',
            },
          }}
          position="bottom-start">
          <Popover.Target>
            <Option
              first
              onClick={() => showCalendar(true)}
              hover={isShowingCalendar}
              active={filter === FilterEnum.custom}>
              <CalendarIcon />
              {startDate && endDate ? (
                <>
                  <label>
                    {format(new Date(startDate), "LLL dd',' yyyy", {
                      locale: ptBR,
                    })}
                  </label>{' '}
                  -{' '}
                  <label>
                    {format(new Date(endDate), "LLL dd',' yyyy", {
                      locale: ptBR,
                    })}
                  </label>
                </>
              ) : (
                'Customizado'
              )}
            </Option>
          </Popover.Target>
          <Popover.Dropdown>
            <Calendar
              {...{
                confirm: (startDate, endDate) =>
                  submitFilter(FilterEnum.custom, {startDate, endDate}),
                startDate,
                endDate,
              }}
            />
          </Popover.Dropdown>
        </Popover>
        <Option
          onClick={() => submitFilter(FilterEnum.yesterday)}
          active={filter === FilterEnum.yesterday}>
          Ontem
        </Option>
        <Option
          onClick={() => submitFilter(FilterEnum.today)}
          active={filter === FilterEnum.today}>
          Hoje
        </Option>
        <Option
          last
          onClick={() => submitFilter(FilterEnum.last3hours)}
          active={filter === FilterEnum.last3hours}>
          Últimas 3 horas
        </Option>
      </Container>
    </Row>
  );
};

export default DateFilter;
